import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { AppLayout } from "./components/layout/AppLayout";
import { Spinner } from "./components/ui/primitives";
import { ImageEditGate } from "./components/ui/ImageEditor";
import { isLocalhost } from "./lib/isLocalhost";

const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const ActivateAccountPage = lazy(() => import("./pages/auth/ActivateAccountPage"));

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CrmDashboardPage = lazy(() => import("./pages/dashboard/CrmDashboardPage"));
const HrmsDashboardPage = lazy(() => import("./pages/dashboard/HrmsDashboardPage"));
const BoardsListPage = lazy(() => import("./pages/boards/BoardsListPage"));
// Old per-service page: the Projects page now filters by service itself, so bookmarks/links land there.
function ServiceProjectsRedirect() {
  const { serviceId } = useParams<{ serviceId: string }>();
  return <Navigate to={`/workflow/boards?service=${serviceId ?? ""}`} replace />;
}
const BoardKanbanPage = lazy(() => import("./pages/boards/BoardKanbanPage"));
const EnquiryListPage = lazy(() => import("./pages/boards/EnquiryListPage"));
const EstimationPage = lazy(() => import("./pages/boards/EstimationPage"));
const AccountsPage = lazy(() => import("./pages/boards/AccountsPage"));
const AccountsDashboardPage = lazy(() => import("./pages/finance/accounts/AccountsDashboardPage"));
const AmcPaymentsPage = lazy(() => import("./pages/finance/accounts/AmcPaymentsPage"));
const ProcurementListPage = lazy(() => import("./pages/procurement/ProcurementListPage"));
const MyTasksPage = lazy(() => import("./pages/MyTasksPage"));
const TeamWorkloadPage = lazy(() => import("./pages/TeamWorkloadPage"));
const FollowUpWorkloadPage = lazy(() => import("./pages/FollowUpWorkloadPage"));
const QuotationEditorPage = lazy(() => import("./pages/tasks/QuotationEditorPage"));
const QuotationViewerPage = lazy(() => import("./pages/tasks/QuotationViewerPage"));
const TimeLogsPage = lazy(() => import("./pages/TimeLogsPage"));
const RecentActivityPage = lazy(() => import("./pages/RecentActivityPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const CustomersListPage = lazy(() => import("./pages/customers/CustomersListPage"));
const CustomerDetailPage = lazy(() => import("./pages/customers/CustomerDetailPage"));
const ErpHomePage = lazy(() => import("./pages/erp/ErpHomePage"));
// ERP registers (localhost preview): read-only views over Procurement, Projects and Estimation.
const ErpOverviewPage = lazy(() => import("./pages/erp/ErpOverviewPage"));
const PurchaseOrdersPage = lazy(() => import("./pages/erp/PurchaseOrdersPage"));
const MaterialRequestsPage = lazy(() => import("./pages/erp/MaterialRequestsPage"));
const DeliveryNotesPage = lazy(() => import("./pages/erp/DeliveryNotesPage"));
const QuotationsPage = lazy(() => import("./pages/erp/QuotationsPage"));
// Inventory (localhost preview): items, warehouses, stock documents, counts, batch/serial and reports.
const InventoryItemsPage = lazy(() => import("./pages/erp/inventory/InventoryItemsPage"));
const WarehousesPage = lazy(() => import("./pages/erp/inventory/WarehousesPage"));
const StockDocumentsPage = lazy(() => import("./pages/erp/inventory/StockDocumentsPage"));
const StockCountPage = lazy(() => import("./pages/erp/inventory/StockCountPage"));
const BatchSerialPage = lazy(() => import("./pages/erp/inventory/BatchSerialPage"));
const StockReportsPage = lazy(() => import("./pages/erp/inventory/StockReportsPage"));
const ScanPage = lazy(() => import("./pages/erp/inventory/ScanPage"));
// Finance (localhost preview): ledger-backed receivables, payables, payments, expenses, tax, bank and reports.
const FinanceOverviewPage = lazy(() => import("./pages/finance/FinanceOverviewPage"));
const ReceivablesPage = lazy(() => import("./pages/finance/ReceivablesPage"));
const PayablesPage = lazy(() => import("./pages/finance/PayablesPage"));
const ChequesPage = lazy(() => import("./pages/finance/ChequesPage"));
const GeneralLedgerPage = lazy(() => import("./pages/finance/GeneralLedgerPage"));
const CashLedgerPage = lazy(() => import("./pages/finance/CashLedgerPage"));
const VoidedTransactionsPage = lazy(() => import("./pages/finance/VoidedTransactionsPage"));
const FinancePaymentsPage = lazy(() => import("./pages/finance/PaymentsPage"));
const ModuleAuditTrailsPage = lazy(() => import("./pages/settings/ModuleAuditTrailsPage"));
const FinanceExpensesPage = lazy(() => import("./pages/finance/ExpensesPage"));
const FinanceTaxesPage = lazy(() => import("./pages/finance/TaxesPage"));
const BankCashPage = lazy(() => import("./pages/finance/BankCashPage"));
const FundsReimbursementsPage = lazy(() => import("./pages/finance/FundsReimbursementsPage"));
const FinancialReportsPage = lazy(() => import("./pages/finance/FinancialReportsPage"));
const TaxInvoicePage = lazy(() => import("./pages/finance/generate/TaxInvoicePage"));
const ProformaPage = lazy(() => import("./pages/finance/generate/ProformaPage"));
const StatementPage = lazy(() => import("./pages/finance/generate/StatementPage"));
const RetentionPage = lazy(() => import("./pages/finance/generate/RetentionPage"));
const MyProfilePage = lazy(() => import("./pages/settings/MyProfilePage"));
const UsersSettingsPage = lazy(() => import("./pages/settings/UsersSettingsPage"));
const EmployeesSettingsPage = lazy(() => import("./pages/settings/EmployeesSettingsPage"));
const RolesSettingsPage = lazy(() => import("./pages/settings/RolesSettingsPage"));
const OrganisationPage = lazy(() => import("./pages/settings/OrganisationPage"));
const TagsSettingsPage = lazy(() => import("./pages/settings/TagsSettingsPage"));
const NotificationSettingsPage = lazy(() => import("./pages/settings/NotificationSettingsPage"));
const AuditTrailPage = lazy(() => import("./pages/settings/AuditTrailPage"));
const BackupSettingsPage = lazy(() => import("./pages/settings/BackupSettingsPage"));
const RequestPage = lazy(() => import("./pages/RequestPage"));
// HRMS expansion (request 1008) — localhost-only until deployed.
const EmployeeDetailPage = lazy(() => import("./pages/hrms/EmployeeDetailPage"));
const EmployeeAssetsPage = lazy(() => import("./pages/hrms/EmployeeAssetsPage"));
const ToolsFolderPage = lazy(() => import("./pages/hrms/ToolsFolderPage")); // request 1243
const CompanyDocumentsPage = lazy(() => import("./pages/hrms/CompanyDocumentsPage"));
const ContactDirectoryPage = lazy(() => import("./pages/hrms/ContactDirectoryPage"));
const KeyPeoplePage = lazy(() => import("./pages/hrms/KeyPeoplePage")); // request 1254
const AirTicketsPage = lazy(() => import("./pages/hrms/AirTicketsPage"));
const LeaveSummaryPage = lazy(() => import("./pages/LeaveSummaryPage")); // HRMS → Leave Summary (request 1075)
const CelebrationsPage = lazy(() => import("./pages/hrms/CelebrationsPage")); // request 1165
const MyAmcWorkPage = lazy(() => import("./pages/amc/MyAmcWorkPage")); // request 1104: Tools → Emergency work
// request 1107: Calendar & Reminders
const CalendarPage = lazy(() => import("./pages/calendar/CalendarPage"));
const MyRemindersPage = lazy(() => import("./pages/calendar/MyRemindersPage"));
const UpcomingEventsPage = lazy(() => import("./pages/calendar/UpcomingEventsPage"));
const CalendarSettingsPage = lazy(() => import("./pages/calendar/CalendarSettingsPage"));
const AmcPage = lazy(() => import("./pages/amc/AmcPage"));
const AmcRecordPage = lazy(() => import("./pages/amc/AmcRecordPage"));
const PasswordManagerPage = lazy(() => import("./pages/hrms/PasswordManagerPage"));
const GenerateDocumentsPage = lazy(() => import("./pages/hrms/GenerateDocumentsPage"));
const CandidatesPage = lazy(() => import("./pages/hrms/recruitment/CandidatesPage"));
const CvBankPage = lazy(() => import("./pages/hrms/recruitment/CvBankPage"));
const InterviewsPage = lazy(() => import("./pages/hrms/recruitment/InterviewsPage"));
const SelectionPage = lazy(() => import("./pages/hrms/recruitment/SelectionPage"));
const OfferLettersPage = lazy(() => import("./pages/hrms/recruitment/OfferLettersPage"));
const JoiningPage = lazy(() => import("./pages/hrms/recruitment/JoiningPage"));
const SalaryStructurePage = lazy(() => import("./pages/hrms/payroll/SalaryStructurePage"));
const AttendancePage = lazy(() => import("./pages/hrms/AttendancePage"));
const LabourAttendancePage = lazy(() => import("./pages/hrms/LabourAttendancePage")); // request 1088
const PayrollProcessingPage = lazy(() => import("./pages/hrms/payroll/PayrollProcessingPage"));
const PayslipsPage = lazy(() => import("./pages/hrms/payroll/PayslipsPage"));
const SalaryReportsPage = lazy(() => import("./pages/hrms/payroll/SalaryReportsPage"));
const EmployeeReportPage = lazy(() => import("./pages/hrms/reports/EmployeeReportPage"));
const AttendanceReportPage = lazy(() => import("./pages/hrms/reports/AttendanceReportPage"));
const LeaveReportPage = lazy(() => import("./pages/hrms/reports/LeaveReportPage"));
const PayrollReportPage = lazy(() => import("./pages/hrms/reports/PayrollReportPage"));
const RecruitmentReportPage = lazy(() => import("./pages/hrms/reports/RecruitmentReportPage"));
const TicketsPage = lazy(() => import("./pages/TicketsPage"));
const EmployeeDashboardsPage = lazy(() => import("./pages/tools/EmployeeDashboardsPage")); // request 1181
const DashboardViewFrame = lazy(() => import("./pages/tools/DashboardViewFrame")); // request 1181
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

/** request 1151: a redirect that keeps the query (?task=… opens that task on the page it lands on) */
function RedirectKeepQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  // Sends logged-out visitors hitting an internal URL directly (e.g. someone
  // probing /dashboard) to the public home page, not the login page — the
  // login URL is intentionally hard to guess, so this path must never reveal it.
  if (!user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <>
    <ImageEditGate />
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login-portal-uae2026" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/activate" element={<ActivateAccountPage />} />
        {/* request 1181: one person's dashboard, view only — shown inside Tools → Employee Dashboards */}
        <Route
          path="/dashboard-view/frame/:userId"
          element={
            <RequireAuth>
              <DashboardViewFrame />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* Per-module dashboards — localhost-only preview, reached by clicking
              a module name in the sidebar (see Sidebar.tsx's ModuleGroup). */}
          {isLocalhost && <Route path="/crm" element={<CrmDashboardPage />} />}
          {isLocalhost && <Route path="/hrms" element={<HrmsDashboardPage />} />}
          <Route path="/workflow/boards" element={<BoardsListPage />} />
          <Route path="/workflow/amc" element={<AmcPage />} />
          <Route path="/workflow/amc/:amcRecordId" element={<AmcRecordPage />} />
          <Route path="/workflow/boards/service/:serviceId" element={<ServiceProjectsRedirect />} />
          <Route path="/workflow/boards/:boardId" element={<BoardKanbanPage />} />
          <Route path="/workflow/enquiries" element={<EnquiryListPage />} />
          <Route path="/workflow/estimation" element={<RedirectKeepQuery to="/workflow/estimation/active" />} />
          <Route path="/workflow/estimation/active" element={<EstimationPage view="active" />} />
          <Route path="/workflow/estimation/follow-up" element={<EstimationPage view="follow-up" />} />
          {/* request 1136: Accounts moved from Workflow to Finance → Accounts */}
          <Route path="/workflow/accounts" element={<Navigate to="/finance/accounts/sign-off" replace />} />
          <Route path="/finance/accounts" element={<AccountsDashboardPage />} />
          <Route path="/finance/accounts/sign-off" element={<AccountsPage section="signoff" />} />
          <Route path="/finance/accounts/settlements" element={<AccountsPage section="settlement" />} />
          <Route path="/finance/accounts/amc-payments" element={<AmcPaymentsPage />} />
          <Route path="/workflow/procurement" element={<ProcurementListPage />} />
          {/* Same BoardKanbanPage as /workflow/boards/:boardId, just under the
              Procurement path so the sidebar highlights "Procurement" (NavLink
              matches by prefix) instead of "Projects" while viewing it. */}
          <Route path="/workflow/procurement/:boardId" element={<BoardKanbanPage />} />
          <Route path="/workflow/my-tasks" element={<MyTasksPage />} />
          <Route path="/workflow/team" element={<TeamWorkloadPage />} />
          <Route path="/workflow/follow-ups" element={<FollowUpWorkloadPage />} />
          <Route path="/workflow/tasks/:taskId/quotation" element={<QuotationEditorPage />} />
          <Route path="/workflow/tasks/:taskId/quotations" element={<QuotationViewerPage />} />
          <Route path="/workflow/time-logs" element={<TimeLogsPage />} />
          <Route path="/workflow/activity" element={<RecentActivityPage />} />
          <Route path="/workflow/history" element={<HistoryPage />} />
          <Route path="/workflow/customers" element={<CustomersListPage />} />
          <Route path="/workflow/customers/:customerId" element={<CustomerDetailPage />} />
          <Route path="/erp" element={<ErpHomePage />} />
          <Route path="/erp/overview" element={<ErpOverviewPage />} />
          <Route path="/erp/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/erp/material-requests" element={<MaterialRequestsPage />} />
          <Route path="/erp/delivery-notes" element={<DeliveryNotesPage />} />
          <Route path="/erp/quotations" element={<QuotationsPage />} />
          <Route path="/erp/inventory/scan" element={<ScanPage />} />
          <Route path="/erp/inventory/items" element={<InventoryItemsPage />} />
          <Route path="/erp/inventory/warehouses" element={<WarehousesPage />} />
          <Route path="/erp/inventory/stock-in" element={<StockDocumentsPage type="STOCK_IN" />} />
          <Route path="/erp/inventory/stock-out" element={<StockDocumentsPage type="STOCK_OUT" />} />
          <Route path="/erp/inventory/stock-transfer" element={<StockDocumentsPage type="TRANSFER" />} />
          <Route path="/erp/inventory/stock-adjustment" element={<StockDocumentsPage type="ADJUSTMENT" />} />
          <Route path="/erp/inventory/stock-count" element={<StockCountPage />} />
          <Route path="/erp/inventory/batches" element={<BatchSerialPage />} />
          <Route path="/erp/inventory/reports" element={<StockReportsPage />} />
          <Route path="/finance" element={<FinanceOverviewPage />} />
          <Route path="/finance/receivables" element={<ReceivablesPage />} />
          <Route path="/finance/payables" element={<PayablesPage />} />
          <Route path="/finance/cheques" element={<ChequesPage />} />
          <Route path="/finance/ledger" element={<GeneralLedgerPage />} />
          {/* request 1149 */}
          <Route path="/finance/ledger/bank" element={<CashLedgerPage kind="BANK" />} />
          <Route path="/finance/ledger/petty-cash" element={<CashLedgerPage kind="CASH" />} />
          <Route path="/finance/ledger/voided" element={<VoidedTransactionsPage />} />
          <Route path="/finance/payments" element={<FinancePaymentsPage />} />
          <Route path="/finance/audit-trail" element={<Navigate to="/tools/audit-trails?module=FINANCE" replace />} /> {/* request 1286: moved */}
          <Route path="/tools/audit-trails" element={<ModuleAuditTrailsPage />} />
          <Route path="/finance/owed" element={<FundsReimbursementsPage />} />
          <Route path="/finance/expenses" element={<FinanceExpensesPage />} />
          <Route path="/finance/taxes" element={<FinanceTaxesPage />} />
          <Route path="/finance/bank" element={<BankCashPage />} />
          <Route path="/finance/reports" element={<FinancialReportsPage />} />
          <Route path="/finance/receivables/tax-invoice" element={<TaxInvoicePage />} />
          <Route path="/finance/receivables/proforma" element={<ProformaPage />} />
          <Route path="/finance/receivables/statement" element={<StatementPage />} />
          <Route path="/finance/receivables/retention" element={<RetentionPage />} />
          <Route path="/hrms/leave" element={<RequestPage />} />
          <Route path="/hrms/employees/:employeeId" element={<EmployeeDetailPage />} />
          <Route path="/hrms/employee-assets" element={<EmployeeAssetsPage />} />
          <Route path="/hrms/tools" element={<ToolsFolderPage />} />
          <Route path="/hrms/company-documents" element={<Navigate to="/hrms/company-documents/dubai" replace />} />
          <Route path="/hrms/company-documents/:branch" element={<CompanyDocumentsPage />} />
          <Route path="/hrms/contact-directory" element={<ContactDirectoryPage />} />
          <Route path="/hrms/owner-details" element={<KeyPeoplePage folder="owner" key="owner" />} />
          <Route path="/hrms/management-family" element={<KeyPeoplePage folder="family" key="family" />} />
          <Route path="/hrms/air-tickets" element={<AirTicketsPage />} />
          <Route path="/hrms/leave-summary" element={<LeaveSummaryPage />} />
          <Route path="/hrms/celebrations" element={<CelebrationsPage />} />
          <Route path="/amc-work" element={<MyAmcWorkPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/calendar/reminders" element={<MyRemindersPage />} />
          <Route path="/calendar/upcoming" element={<UpcomingEventsPage />} />
          <Route path="/calendar/settings" element={<CalendarSettingsPage />} />
          <Route path="/hrms/password-manager" element={<PasswordManagerPage />} />
          <Route path="/hrms/generate" element={<GenerateDocumentsPage />} />
          <Route path="/hrms/recruitment" element={<Navigate to="/hrms/recruitment/candidates" replace />} />
          <Route path="/hrms/recruitment/candidates" element={<CandidatesPage />} />
          <Route path="/hrms/recruitment/cv-bank" element={<CvBankPage />} />
          <Route path="/hrms/recruitment/interviews" element={<InterviewsPage />} />
          <Route path="/hrms/recruitment/selection" element={<SelectionPage />} />
          <Route path="/hrms/recruitment/offers" element={<OfferLettersPage />} />
          <Route path="/hrms/recruitment/joining" element={<JoiningPage />} />
          <Route path="/hrms/payroll" element={<Navigate to="/hrms/payroll/structure" replace />} />
          <Route path="/hrms/payroll/structure" element={<SalaryStructurePage />} />
          <Route path="/hrms/attendance" element={<AttendancePage />} />
          <Route path="/hrms/labour-attendance" element={<LabourAttendancePage />} />
          <Route path="/hrms/payroll/processing" element={<PayrollProcessingPage />} />
          <Route path="/hrms/payroll/payslips" element={<PayslipsPage />} />
          <Route path="/hrms/payroll/reports" element={<SalaryReportsPage />} />
          <Route path="/hrms/reports" element={<Navigate to="/hrms/reports/employees" replace />} />
          <Route path="/hrms/reports/employees" element={<EmployeeReportPage />} />
          <Route path="/hrms/reports/attendance" element={<AttendanceReportPage />} />
          <Route path="/hrms/reports/overtime" element={<Navigate to="/hrms/attendance?tab=overtime" replace />} />
          <Route path="/hrms/reports/leave" element={<LeaveReportPage />} />
          <Route path="/hrms/reports/payroll" element={<PayrollReportPage />} />
          <Route path="/hrms/reports/recruitment" element={<RecruitmentReportPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tools/employee-dashboards" element={<EmployeeDashboardsPage />} />
          <Route path="/settings/profile" element={<MyProfilePage />} />
          <Route path="/settings/users" element={<UsersSettingsPage />} />
          <Route path="/settings/employees" element={<EmployeesSettingsPage />} />
          <Route path="/settings/roles" element={<RolesSettingsPage />} />
          <Route path="/settings/organisation" element={<OrganisationPage />} />
          <Route path="/settings/tags" element={<TagsSettingsPage />} />
          <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
          <Route path="/settings/audit" element={<AuditTrailPage />} />
          <Route path="/settings/backup" element={<BackupSettingsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </>
  );
}
